import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  Filter,
  Package,
  Book,
  Shield,
  HelpCircle,
  FileCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';

interface SiteDocument {
  id: string;
  site_id: string;
  title: string;
  content: string;
  document_type: string;
  metadata: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Site {
  id?: string;
  _id?: string;
  name: string;
  domain?: string;
  url?: string;
}

const documentTypeIcons = {
  product: Package,
  technical: FileCode,
  policy: Shield,
  faq: HelpCircle,
  general: FileText
};

const documentTypeLabels = {
  product: 'Product Information',
  technical: 'Technical Documentation',
  policy: 'Policies & Procedures',
  faq: 'FAQ',
  general: 'General Information'
};

export default function SiteDocuments() {
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [documents, setDocuments] = useState<SiteDocument[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<SiteDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<SiteDocument | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    document_type: 'general',
    metadata: {}
  });
  const { toast } = useToast();

  // Fetch sites on mount
  useEffect(() => {
    fetchSites();
  }, []);

  // Fetch documents when site is selected
  useEffect(() => {
    if (selectedSiteId) {
      fetchDocuments();
    }
  }, [selectedSiteId]);

  // Filter documents when search or filter changes
  useEffect(() => {
    let filtered = [...documents];
    
    if (searchTerm) {
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(doc => doc.document_type === filterType);
    }
    
    setFilteredDocuments(filtered);
  }, [documents, searchTerm, filterType]);

  const fetchSites = async () => {
    try {
      const response = await fetch('/api/sites');
      const data = await response.json();
      // API returns array directly
      if (Array.isArray(data)) {
        setSites(data);
        // Auto-select first site if available
        if (data.length > 0 && !selectedSiteId) {
          setSelectedSiteId(data[0].id || data[0]._id); // Handle both id and _id
        }
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sites',
        variant: 'destructive'
      });
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/site-documents/site/${selectedSiteId}`);
      const data = await response.json();
      if (data.success) {
        setDocuments(data.documents);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch('/api/site-documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSiteId,
          ...formData
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Document created successfully'
        });
        setIsCreateDialogOpen(false);
        resetForm();
        fetchDocuments();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error creating document:', error);
      toast({
        title: 'Error',
        description: 'Failed to create document',
        variant: 'destructive'
      });
    }
  };

  const handleUpdate = async () => {
    if (!editingDocument) return;
    
    try {
      const response = await fetch(`/api/site-documents/${editingDocument.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Document updated successfully'
        });
        setIsEditDialogOpen(false);
        setEditingDocument(null);
        resetForm();
        fetchDocuments();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error updating document:', error);
      toast({
        title: 'Error',
        description: 'Failed to update document',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const response = await fetch(`/api/site-documents/${documentId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Document deleted successfully'
        });
        fetchDocuments();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (document: SiteDocument) => {
    setEditingDocument(document);
    setFormData({
      title: document.title,
      content: document.content,
      document_type: document.document_type,
      metadata: document.metadata || {}
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      document_type: 'general',
      metadata: {}
    });
  };

  const selectedSite = sites.find(s => (s.id || s._id) === selectedSiteId);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Site Documents</h1>
        <p className="text-gray-600">Manage private documents and knowledge base for each site</p>
      </div>

      {/* Site Selector */}
      <div className="mb-6">
        <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
          <SelectTrigger className="w-full md:w-96">
            <SelectValue placeholder="Select a site" />
          </SelectTrigger>
          <SelectContent>
            {sites.map(site => {
              const siteId = site.id || site._id || '';
              const siteDomain = site.domain || site.url || '';
              return (
                <SelectItem key={siteId} value={siteId}>
                  {site.name} ({siteDomain})
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {selectedSiteId && (
        <>
          {/* Filters and Actions */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="product">Product Information</SelectItem>
                  <SelectItem value="technical">Technical Docs</SelectItem>
                  <SelectItem value="policy">Policies</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Document
            </Button>
          </div>

          {/* Site Info Card */}
          {selectedSite && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedSite.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  <p>Domain: {selectedSite.domain || selectedSite.url}</p>
                  <p>Documents: {documents.length}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents Grid */}
          {loading ? (
            <div className="text-center py-12">Loading documents...</div>
          ) : filteredDocuments.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm || filterType !== 'all' 
                    ? 'No documents found matching your criteria'
                    : 'No documents yet. Add your first document to get started.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredDocuments.map(doc => {
                const Icon = documentTypeIcons[doc.document_type as keyof typeof documentTypeIcons] || FileText;
                return (
                  <Card key={doc.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Icon className="h-5 w-5 text-gray-600 mt-1" />
                          <div className="flex-1">
                            <CardTitle className="text-lg line-clamp-1">
                              {doc.title}
                            </CardTitle>
                            <p className="text-sm text-gray-500 mt-1">
                              {documentTypeLabels[doc.document_type as keyof typeof documentTypeLabels]}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(doc)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {doc.content}
                      </p>
                      <p className="text-xs text-gray-400 mt-4">
                        Updated {new Date(doc.updated_at).toLocaleDateString()}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Site Document</DialogTitle>
            <DialogDescription>
              Create a new document for {selectedSite?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Product Specifications, Return Policy"
              />
            </div>
            <div>
              <Label htmlFor="type">Document Type</Label>
              <Select 
                value={formData.document_type} 
                onValueChange={(value) => setFormData({...formData, document_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Product Information</SelectItem>
                  <SelectItem value="technical">Technical Documentation</SelectItem>
                  <SelectItem value="policy">Policies & Procedures</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="general">General Information</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Enter the document content..."
                rows={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!formData.title || !formData.content}>
              Create Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update document for {selectedSite?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-type">Document Type</Label>
              <Select 
                value={formData.document_type} 
                onValueChange={(value) => setFormData({...formData, document_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Product Information</SelectItem>
                  <SelectItem value="technical">Technical Documentation</SelectItem>
                  <SelectItem value="policy">Policies & Procedures</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="general">General Information</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-content">Content</Label>
              <Textarea
                id="edit-content"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={!formData.title || !formData.content}>
              Update Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}